import React from 'react';
import { ChangelogEntry, TipTapMark, TipTapNode } from './types';

export const LAST_SEEN_CHANGELOG_KEY = 'whats-new-last-seen-id';

/** Cap how many missed updates the announcement modal steps through. */
const MAX_ANNOUNCEMENT_ENTRIES = 5;

/**
 * Launch gate: only changelogs published on/after this date are announced by
 * the modal. Entries that already existed before the modal shipped must not
 * pop up for users with no last-seen record (first visit, new device, cleared
 * storage). The sidebar tooltip is intentionally not gated — it predates this.
 */
const ANNOUNCEMENT_CUTOFF_DATE = '2026-07-02';

function isAnnounceable(entry: ChangelogEntry): boolean {
  // ISO 8601 strings compare correctly lexicographically
  // ('2026-07-02T09:00:00.000Z' >= '2026-07-02').
  return Boolean(entry.date) && entry.date >= ANNOUNCEMENT_CUTOFF_DATE;
}

/**
 * Entries newer than the last-seen id, oldest-first so the modal reads
 * chronologically. `published` is expected newest-first (Productlane order).
 * An unknown or absent last-seen id falls back to just the latest entry so
 * first-time visitors aren't walked through the whole backlog. Entries dated
 * before the launch cutoff are never announced.
 */
export function getUnseenEntries(
  published: ChangelogEntry[],
  lastSeenId: string | null
): ChangelogEntry[] {
  if (published.length === 0) return [];
  const lastSeenIndex =
    lastSeenId == null
      ? -1
      : published.findIndex((entry) => entry.id === lastSeenId);
  if (lastSeenIndex === -1) {
    const latest = published[0];
    return isAnnounceable(latest) ? [latest] : [];
  }
  return published
    .slice(0, lastSeenIndex)
    .filter(isAnnounceable)
    .slice(0, MAX_ANNOUNCEMENT_ENTRIES)
    .reverse();
}

/** Stable key for a node (no array index) to satisfy react/no-array-index-key. */
function nodeKey(node: TipTapNode): string {
  const str = JSON.stringify({
    type: node.type,
    text: node.text,
    attrs: node.attrs,
  });
  // Bitwise ops are intentional – standard djb2 hash to produce a 32-bit int.
  /* eslint-disable no-bitwise */
  const h = str.split('').reduce(
    (acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0,
    0
  );
  /* eslint-enable no-bitwise */
  return `n-${h}`;
}

/**
 * Content-hash keys collide for identical siblings (e.g. two hardBreaks in
 * one paragraph), so each level dedupes repeats with an occurrence counter —
 * still stable across re-renders, unlike a bare array index.
 */
function createNodeKeyer(parentKey: string) {
  const seen = new Map<string, number>();
  return (node: TipTapNode) => {
    const base = `${parentKey}-${nodeKey(node)}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  };
}

function applyMark(content: React.ReactNode, mark: TipTapMark): React.ReactNode {
  if (mark.type === 'bold')
    return (
      <strong className="font-semibold text-text-primary">{content}</strong>
    );
  if (mark.type === 'italic') return <em>{content}</em>;
  if (mark.type === 'code')
    return (
      <code className="bg-bg-secondary border border-border-primary text-text-primary font-mono text-[0.8125rem] px-1 py-0.5 rounded">
        {content}
      </code>
    );
  if (mark.type === 'link') {
    const href = (mark.attrs?.href as string) ?? '#';
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fg-brand hover:underline"
      >
        {content}
      </a>
    );
  }
  return content;
}

function renderInlineContent(node: TipTapNode): React.ReactNode {
  if (node.type === 'text') {
    const base: React.ReactNode = node.text ?? '';
    return (node.marks ?? []).reduce(
      (acc: React.ReactNode, mark) => applyMark(acc, mark),
      base
    );
  }
  if (node.type === 'hardBreak') return <br />;
  return null;
}

export function renderNodes(
  nodes: TipTapNode[],
  parentKey = 'r'
): React.ReactNode[] {
  const keyFor = createNodeKeyer(parentKey);
  return nodes.map((node) => {
    const key = keyFor(node);

    switch (node.type) {
      case 'paragraph': {
        const inlineKeyFor = createNodeKeyer(key);
        return (
          <p
            key={key}
            className="text-text-secondary leading-relaxed [&:not(:first-child)]:mt-3"
          >
            {node.content?.map((child) => (
              <React.Fragment key={inlineKeyFor(child)}>
                {renderInlineContent(child)}
              </React.Fragment>
            ))}
          </p>
        );
      }

      case 'heading': {
        const level = (node.attrs?.level as number) ?? 2;
        const inlineKeyFor = createNodeKeyer(key);
        const children = node.content?.map((child) => (
          <React.Fragment key={inlineKeyFor(child)}>
            {renderInlineContent(child)}
          </React.Fragment>
        ));
        if (level === 1)
          return (
            <h2
              key={key}
              className="text-lg font-semibold text-text-primary mt-5 mb-2"
            >
              {children}
            </h2>
          );
        if (level === 2)
          return (
            <h3
              key={key}
              className="text-base font-semibold text-text-primary mt-4 mb-1.5"
            >
              {children}
            </h3>
          );
        return (
          <h4
            key={key}
            className="text-sm font-semibold text-text-primary mt-3 mb-1"
          >
            {children}
          </h4>
        );
      }

      case 'bulletList':
        return (
          <ul
            key={key}
            className="list-disc pl-5 mt-2 space-y-1.5 text-text-secondary marker:text-text-quaternary"
          >
            {node.content && renderNodes(node.content, key)}
          </ul>
        );

      case 'orderedList':
        return (
          <ol
            key={key}
            className="list-decimal pl-5 mt-2 space-y-1.5 text-text-secondary"
          >
            {node.content && renderNodes(node.content, key)}
          </ol>
        );

      case 'listItem':
        return (
          <li key={key} className="leading-relaxed">
            {node.content && renderNodes(node.content, key)}
          </li>
        );

      case 'horizontalRule':
        return <hr key={key} className="border-border-primary my-4" />;

      case 'image': {
        const src = node.attrs?.src as string;
        if (!src) return null;
        return (
          <img
            key={key}
            src={src}
            alt=""
            className="my-4 w-full rounded-md border border-border-primary shadow-sm"
            loading="lazy"
          />
        );
      }

      case 'blockquote':
        return (
          <blockquote
            key={key}
            className="border-l-4 border-border-primary bg-bg-secondary rounded-r-md px-4 py-2 my-3"
          >
            {node.content && renderNodes(node.content, key)}
          </blockquote>
        );

      case 'codeBlock':
        return (
          <pre
            key={key}
            className="bg-bg-secondary border border-border-primary rounded-md p-4 my-3 overflow-x-auto text-sm font-mono text-text-primary"
          >
            <code>{node.content?.map((child) => child.text).join('')}</code>
          </pre>
        );

      default:
        // Fallback: try to render children
        if (node.content)
          return (
            <React.Fragment key={key}>
              {renderNodes(node.content, key)}
            </React.Fragment>
          );
        return null;
    }
  });
}
